import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { REGIONS } from "@/lib/content";
import { LogoImage } from "@/components/Nav";

const Footer = () => (
  <footer className="border-t border-white/[0.08] bg-[#070C0D]" data-testid="site-footer">
    <div className="nx-container grid gap-12 py-16 lg:grid-cols-12 lg:py-20">
      <div className="lg:col-span-4">
        <Link to="/" data-testid="footer-logo" aria-label="NEXREQ — Home">
          <LogoImage className="h-20 sm:h-24" loading="lazy" />
        </Link>
        <p className="mt-4 font-heading text-xl font-light text-white/70">
          Your People Partner in Every Hire.
        </p>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/45">
          A North American recruitment organization serving employers and professionals across the
          United States, Canada, the UAE and the wider GCC.
        </p>
      </div>

      <div className="lg:col-span-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">Explore</p>
        <ul className="mt-4 space-y-2.5 text-sm">
          {[
            ["For Employers", "/employers"],
            ["For Candidates", "/candidates"],
            ["Solutions", "/solutions"],
            ["Industries", "/industries"],
            ["Jobs", "/jobs"],
            ["About", "/about"],
            ["Insights", "/insights"],
            ["Contact", "/contact"],
          ].map(([label, to]) => (
            <li key={to}>
              <Link
                to={to}
                data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-white/55 transition-colors hover:text-emerald-400"
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">Solutions</p>
        <ul className="mt-4 space-y-2.5 text-sm">
          {["Direct Hire", "Executive Search", "Recruitment Process Outsourcing", "Talent Advisory"].map(
            (s) => (
              <li key={s}>
                <Link
                  to="/solutions"
                  data-testid={`footer-solution-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-white/55 transition-colors hover:text-emerald-400"
                >
                  {s}
                </Link>
              </li>
            )
          )}
        </ul>
      </div>

      <div className="lg:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">Contact</p>
        <div className="mt-4 space-y-3 text-sm">
          <a
            href="mailto:info@nexreq.ae"
            data-testid="footer-email-link"
            className="group flex items-center gap-2 text-white/55 transition-colors hover:text-emerald-400"
          >
            info@nexreq.ae <ArrowUpRight size={13} className="opacity-0 transition-opacity group-hover:opacity-100" />
          </a>
          <a
            href="https://www.nexreq.ae"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-website-link"
            className="group flex items-center gap-2 text-white/55 transition-colors hover:text-emerald-400"
          >
            www.nexreq.ae <ArrowUpRight size={13} className="opacity-0 transition-opacity group-hover:opacity-100" />
          </a>
          <div className="pt-4">
            <Link to="/hire-talent" className="nx-btn-primary !px-6 !py-3" data-testid="footer-hire-talent-btn">
              Hire Talent
            </Link>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-white/[0.06]" data-testid="global-presence">
      <div className="nx-container py-14 lg:py-16">
        <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          <span className="inline-block h-1.5 w-1.5 rotate-45" style={{ backgroundColor: "#255058" }} />
          Global Presence
        </p>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6" data-testid="office-uae">
            <div
              className="relative h-full border border-white/10 p-8 lg:p-10"
              style={{ backgroundColor: "rgba(37,80,88,0.12)" }}
            >
              <span className="absolute inset-y-0 left-0 w-px" style={{ backgroundColor: "#255058" }} />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                Dubai Office
              </p>
              <p className="mt-5 flex items-center gap-3.5 font-heading text-3xl font-light tracking-wide text-white lg:text-4xl">
                <span className="inline-block h-2 w-2 rotate-45" style={{ backgroundColor: "#255058" }} />
                UAE
              </p>
              <p className="mt-6 text-base leading-relaxed text-white/70 lg:text-lg">
                <span className="block">Bin Dasmal Building – 1</span>
                <span className="block">Al Quoz Industrial First</span>
                <span className="block">Dubai</span>
                <span className="block">United Arab Emirates</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center lg:col-span-6 lg:pl-4">
            <div data-testid="office-united-states">
              <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                <span className="inline-block h-1 w-1 rotate-45" style={{ backgroundColor: "#255058" }} />
                United States
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/40">
                <span className="block">3950 Lee Street</span>
                <span className="block">Skokie, IL 60076</span>
                <span className="block">United States</span>
              </p>
            </div>
            <div className="my-9 h-px bg-white/[0.08]" />
            <div data-testid="office-canada">
              <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                <span className="inline-block h-1 w-1 rotate-45" style={{ backgroundColor: "#255058" }} />
                Canada
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/40">
                <span className="block">First Canadian Place</span>
                <span className="block">100 King Street West, Suite 5600</span>
                <span className="block">Toronto, ON M5X 1C9</span>
                <span className="block">Canada</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-white/[0.06]">
      <div className="nx-container flex flex-wrap gap-x-6 gap-y-2 py-6">
        {REGIONS.map((r) => (
          <span
            key={r}
            className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-white/30"
          >
            <span className="h-1 w-1 rounded-full bg-emerald-600/70" />
            {r}
          </span>
        ))}
      </div>
    </div>

    <div className="border-t border-white/[0.06]">
      <div className="nx-container flex flex-col items-start justify-between gap-3 py-6 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <p className="text-xs text-white/30">© 2026 NEXREQ UAE &amp; GCC. All rights reserved.</p>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-white/30 transition-colors hover:text-emerald-400">Privacy Policy</Link>
          <Link to="/privacy" data-testid="footer-candidate-privacy-link" className="text-xs text-white/30 transition-colors hover:text-emerald-400">Candidate Privacy Notice</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-white/30 transition-colors hover:text-emerald-400">Terms of Use</Link>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/25">
          Global Talent. Regional Expertise.
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
